import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Eye, Monitor, Smartphone, Send, ChevronRight,
  Loader2, X, Copy, Check, RefreshCw, FileText
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const THEME = {
  bg: '#0f1117',
  cardBg: '#1a1d24',
  accent: '#10b981',
  textPrimary: '#f1f5f9',
  textSecondary: '#94a3b8',
  border: '#2d3748'
};

const BUSINESS_TYPES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training', label: 'Training/Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'finance', label: 'Finance/Loans' },
  { value: 'travel', label: 'Travel' },
  { value: 'general', label: 'General' }
];

const EmailTemplatePreview = ({ isOpen, onClose }) => {
  const { businessType: defaultBusinessType } = useBusinessContent();
  const [businessType, setBusinessType] = useState(defaultBusinessType || 'general');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [viewMode, setViewMode] = useState('desktop'); // desktop or mobile
  const [copied, setCopied] = useState(false);

  // Fetch templates for business type
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [businessType, isOpen]);

  // Fetch preview when template selected
  useEffect(() => {
    if (selectedTemplate) {
      fetchPreview();
    }
  }, [selectedTemplate, businessType]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/email-templates/preview/templates-list/${businessType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(res.data.templates || []);
      if (res.data.templates?.length > 0 && !selectedTemplate) {
        setSelectedTemplate(res.data.templates[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/email-templates/preview/rich/${businessType}/${selectedTemplate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(res.data);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error('Failed to load template preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/email-templates/preview/send-test?business_type=${businessType}&template_id=${selectedTemplate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Test email sent! Check ajssngroup@gmail.com');
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleCopyHTML = () => {
    if (preview?.body) {
      navigator.clipboard.writeText(preview.body);
      setCopied(true);
      toast.success('HTML copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-6xl h-[90vh] rounded-xl overflow-hidden flex"
          style={{ backgroundColor: THEME.cardBg }}
          onClick={e => e.stopPropagation()}
        >
          {/* Left Panel - Template List */}
          <div className="w-80 border-r border-gray-700 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-emerald-500" />
                  Template Preview
                </h2>
                <button onClick={onClose} className="p-1.5 hover:bg-gray-700 rounded-lg">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {/* Business Type Selector */}
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto p-2">
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`w-full p-3 rounded-lg text-left mb-2 transition-all ${
                    selectedTemplate === template.id
                      ? 'bg-emerald-500/20 border border-emerald-500/50'
                      : 'bg-gray-800/50 border border-transparent hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className={`w-4 h-4 ${selectedTemplate === template.id ? 'text-emerald-400' : 'text-gray-500'}`} />
                    <span className={`font-medium ${selectedTemplate === template.id ? 'text-emerald-400' : 'text-white'}`}>
                      {template.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {template.subject_preview}
                  </p>
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-700 space-y-2">
              <Button
                onClick={handleSendTest}
                disabled={sendingTest || !selectedTemplate}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {sendingTest ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Test Email</>
                )}
              </Button>
              <Button
                onClick={handleCopyHTML}
                disabled={!preview}
                variant="outline"
                className="w-full border-gray-700 text-gray-300"
              >
                {copied ? (
                  <><Check className="w-4 h-4 mr-2 text-emerald-400" />Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" />Copy HTML</>
                )}
              </Button>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 flex flex-col bg-gray-900">
            {/* Preview Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Subject:</p>
                <p className="text-white font-medium">
                  {preview?.subject || 'Select a template'}
                </p>
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  <Monitor className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  <Smartphone className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-6 flex justify-center">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : preview ? (
                <div
                  className={`bg-white rounded-lg shadow-2xl overflow-auto ${
                    viewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]'
                  }`}
                  style={{ maxHeight: 'calc(100% - 20px)' }}
                >
                  <iframe
                    srcDoc={preview.body}
                    title="Email Preview"
                    className="w-full h-full min-h-[600px] border-0"
                    sandbox="allow-same-origin"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <FileText className="w-16 h-16 mb-4" />
                  <p>Select a template to preview</p>
                </div>
              )}
            </div>

            {/* Sample Data Footer */}
            {preview?.sample_data && (
              <div className="p-4 border-t border-gray-700 bg-gray-800/50">
                <p className="text-xs text-gray-400 mb-2">Sample Data Used:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(preview.sample_data).slice(0, 6).map(([key, value]) => (
                    <Badge key={key} variant="outline" className="text-xs bg-gray-800 border-gray-700 text-gray-300">
                      {key}: {String(value).substring(0, 20)}
                    </Badge>
                  ))}
                  {Object.keys(preview.sample_data).length > 6 && (
                    <Badge variant="outline" className="text-xs bg-gray-800 border-gray-700 text-gray-400">
                      +{Object.keys(preview.sample_data).length - 6} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailTemplatePreview;
